import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Pill from '../components/Pill';
import { submitExcuse } from '../lib/api';

const TYPES = ['THE SOFT BOUNDARY', 'THE PERSONAL TRUTH', 'THE CHAOS CARD'] as const;
const CATS = ['Work', 'Social', 'Family', 'Dating', 'Errands', 'Other'];

const TYPE_LABEL: Record<string, string> = {
  'THE SOFT BOUNDARY': 'Soft boundary',
  'THE PERSONAL TRUTH': 'Personal truth',
  'THE CHAOS CARD': 'Chaos card',
};

export default function Submit() {
  const [text, setText] = useState('');
  const [type, setType] = useState<string>(TYPES[0]);
  const [category, setCategory] = useState<string>(CATS[0]);
  const [status, setStatus] = useState<
    null | 'submitting' | 'approved' | 'pending' | 'rejected'
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState<string | null>(null);
  const navigate = useNavigate();

  const submit = async () => {
    if (!text.trim()) return;
    setStatus('submitting');
    setError(null);
    setRejectReason(null);
    try {
      const result = await submitExcuse({ text: text.trim(), type, category });
      setStatus(result.status);
      if (result.status === 'approved') {
        setTimeout(() => navigate('/hall-of-fame'), 1400);
      } else if (result.status === 'rejected' && result.reason) {
        setRejectReason(result.reason);
      }
    } catch (e) {
      setStatus(null);
      setError(e instanceof Error ? e.message : 'Submit failed.');
    }
  };

  const disabled = !text.trim() || text.trim().length < 40 || status === 'submitting';

  return (
    <div>
      <section className="pad-x" style={{ padding: '80px 40px 40px', maxWidth: 680 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.14em',
            color: 'var(--tag)',
            marginBottom: 24,
            textTransform: 'uppercase',
          }}
        >
          Submit
        </div>
        <h1
          className="hero-title"
          style={{
            fontSize: 44,
            fontWeight: 700,
            letterSpacing: '-0.03em',
            lineHeight: 1.1,
            marginBottom: 20,
          }}
        >
          Pitch your excuse
          <br />
          for the Hall of Fame.
        </h1>
        <p
          style={{
            fontSize: 14,
            color: 'var(--fg-muted)',
            lineHeight: 1.7,
            maxWidth: 440,
          }}
        >
          Anonymous. No names, no contact info, no identifying details. Off-topic or
          abusive submissions get filtered — keep it useful and keep it clean.
        </p>
      </section>

      <section
        className="pad-x"
        style={{
          padding: '24px 40px 80px',
          maxWidth: 680,
          display: 'flex',
          flexDirection: 'column',
          gap: 28,
        }}
      >
        <div>
          <Label>Category</Label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {CATS.map((c) => (
              <Pill
                key={c}
                label={c}
                active={category === c}
                onClick={() => setCategory(c)}
              />
            ))}
          </div>
        </div>

        <div>
          <Label>Type</Label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {TYPES.map((t) => (
              <Pill
                key={t}
                label={TYPE_LABEL[t]}
                active={type === t}
                onClick={() => setType(t)}
              />
            ))}
          </div>
        </div>

        <div>
          <Label>Your excuse</Label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={600}
            placeholder="I can't make it tonight — something has come up that I genuinely cannot explain yet..."
            style={{
              width: '100%',
              minHeight: 150,
              padding: '14px 16px',
              background: 'var(--input-bg)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              color: 'var(--fg)',
              fontFamily: 'inherit',
              fontSize: 14,
              lineHeight: 1.6,
              resize: 'vertical',
            }}
          />
          <div style={{ fontSize: 11, color: 'var(--fg-dim)', marginTop: 6 }}>
            {text.length}/600 {text.trim().length < 40 && '· 40+ characters required'}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={submit}
            disabled={disabled}
            style={{
              padding: '14px 28px',
              background: disabled ? '#2a2828' : 'var(--accent)',
              color: disabled ? '#555' : '#0f0f0f',
              border: 'none',
              borderRadius: 6,
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 600,
              cursor: disabled ? 'default' : 'pointer',
            }}
          >
            {status === 'submitting' ? 'Submitting…' : 'Submit →'}
          </button>
          <Link
            to="/hall-of-fame"
            style={{
              fontSize: 13,
              color: 'var(--fg-muted)',
              textDecoration: 'none',
            }}
          >
            Back to Hall of Fame
          </Link>
        </div>

        {status === 'approved' && (
          <Notice tone="ok">
            Submitted. You'll see it in the Hall of Fame in a sec.
          </Notice>
        )}
        {status === 'pending' && (
          <Notice tone="warn">
            Submitted. It's under quick review and will appear shortly if approved.
          </Notice>
        )}
        {status === 'rejected' && (
          <Notice tone="err">
            We couldn't accept that one (too short, off-topic, or flagged). Try rewriting.
            {rejectReason && <> Reason: <i>{rejectReason}</i></>}
          </Notice>
        )}
        {error && <Notice tone="err">{error}</Notice>}
      </section>
    </div>
  );
}

function Label({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.12em',
        color: 'var(--tag)',
        textTransform: 'uppercase',
        marginBottom: 14,
      }}
    >
      {children}
    </div>
  );
}

function Notice({ tone, children }: { tone: 'ok' | 'warn' | 'err'; children: ReactNode }) {
  const colors = {
    ok: { bg: '#0f2a1a', border: '#1f5c3a', fg: '#8fe29c' },
    warn: { bg: '#2a220f', border: '#5c4a1f', fg: '#e2c890' },
    err: { bg: '#2a1a1a', border: '#5c2a2a', fg: '#e29090' },
  }[tone];
  return (
    <div
      style={{
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        color: colors.fg,
        padding: '12px 16px',
        borderRadius: 6,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}
